/**
 * API client. Anonymous identity: the server issues a bearer token on first
 * contact; we keep it in localStorage. If the API is unreachable the app still
 * plays free episodes (offline mode) and reports `online: false`.
 */
import { readJson, writeJson } from './storage';

export interface ApiUser {
  id: string;
  credits: number;
  createdAt: string;
  flags: Record<string, string>;
  giftCount: number;
}
export interface CreditPack {
  sku: string;
  credits: number;
  priceUsd: number;
  badge?: string;
  label?: string;
}
export interface ProgressSummary {
  seriesId: string;
  episodeId: string;
  nodeId: string;
  endings: string[];
  updatedAt: string;
  completedAt: string | null;
  steps: number;
}
export interface Session {
  token: string;
  created: boolean;
  user: ApiUser;
  unlocks: string[];
  progress: ProgressSummary[];
  packs: CreditPack[];
}

const TOKEN_KEY = 'tales.token.v1';
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: Record<string, unknown>,
  ) {
    super(typeof body['error'] === 'string' ? body['error'] : `http ${status}`);
  }
}

class Api {
  token: string | undefined = readJson<string | undefined>(TOKEN_KEY, undefined);
  online = false;

  private async call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json', ...(init.headers as Record<string, string> | undefined) };
    if (this.token) headers['authorization'] = `Bearer ${this.token}`;
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) throw new ApiError(res.status, body);
    return body as T;
  }

  async startSession(): Promise<Session | undefined> {
    const q = new URLSearchParams(location.search);
    const utm: Record<string, string> = {};
    for (const [k, v] of q) if (k.startsWith('utm_')) utm[k] = v;
    const body = {
      ...(q.get('fbclid') ? { fbclid: q.get('fbclid') } : {}),
      ...(Object.keys(utm).length ? { utm } : {}),
      ...(document.referrer ? { referrer: document.referrer } : {}),
      landing: location.href,
    };
    try {
      const s = await this.call<Session>('/session', { method: 'POST', body: JSON.stringify(body) });
      this.token = s.token;
      writeJson(TOKEN_KEY, s.token);
      this.online = true;
      return s;
    } catch {
      this.online = false;
      return undefined;
    }
  }

  unlock(seriesId: string, episodeId: string) {
    return this.call<{ unlocked: boolean; balance: number; price: number; charged: number }>('/unlock', { method: 'POST', body: JSON.stringify({ seriesId, episodeId }) });
  }

  purchaseIntent(sku: string, seriesId?: string, episodeId?: string, context?: string) {
    return this.call<{ balance: number; gifted: number; first: boolean; message: string }>('/purchase-intent', {
      method: 'POST',
      body: JSON.stringify({ sku, seriesId, episodeId, context }),
    });
  }

  saveProgress(p: { seriesId: string; episodeId: string; nodeId: string; vars: Record<string, number | boolean>; history: { nodeId: string; choiceId?: string; at: number }[]; endingId?: string; seriesVars?: Record<string, number | boolean> }) {
    return this.call<{ ok: true }>('/progress', { method: 'POST', body: JSON.stringify(p) });
  }

  seriesState(seriesId: string) {
    return this.call<{ state: { vars: Record<string, number | boolean>; lastEpisodeId: string } | null }>(`/series-state/${seriesId}`);
  }

  me() {
    return this.call<Omit<Session, 'token' | 'created'>>('/me');
  }

  /** Fire-and-forget batch; uses sendBeacon when the page is going away. */
  sendEvents(events: unknown[], beacon = false): void {
    if (!this.token || !events.length) return;
    const body = JSON.stringify({ events });
    if (beacon && navigator.sendBeacon) {
      // sendBeacon cannot set headers: pass the token in the query string for this one path.
      navigator.sendBeacon(`${BASE}/events?token=${encodeURIComponent(this.token)}`, new Blob([body], { type: 'text/plain' }));
      return;
    }
    void this.call('/events', { method: 'POST', body }).catch(() => undefined);
  }
}

export const api = new Api();
